import { useState } from "react";
import { toast } from "sonner";
import { Heart, HeartCrack } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";

export default function RsvpForm({ slug, wedding }) {
  const [form, setForm] = useState({ nome: "", email: "", telefono: "", conferma: null, num_ospiti_extra: 0, allergie_note: "", moments: [] });
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);
  const askMoments = !!wedding.rsvp_ask_moments && (wedding.moments || []).length > 0;

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggleMoment = (id) => setForm((f) => ({
    ...f,
    moments: f.moments.includes(id) ? f.moments.filter((m) => m !== id) : [...f.moments, id],
  }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.nome.trim()) return toast.error("Inserisci il tuo nome");
    if (form.conferma === null) return toast.error("Indica se parteciperai");
    setSending(true);
    try {
      await api.post(`/public/${slug}/rsvp`, { ...form, num_ospiti_extra: Number(form.num_ospiti_extra) || 0 });
      setDone(true);
      toast.success("RSVP inviato, grazie!");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSending(false);
    }
  };

  if (done)
    return (
      <section className="py-20 sm:py-28 px-6" data-testid="rsvp-section">
        <div className="max-w-xl mx-auto text-center" data-testid="rsvp-success">
          <Heart size={40} className="mx-auto mb-6" style={{ color: "var(--wed-primary)" }} />
          <h2 className="font-wed text-3xl sm:text-4xl mb-3">Grazie!</h2>
          <p className="text-neutral-500">La tua risposta è stata registrata. Non vediamo l'ora!</p>
        </div>
      </section>
    );

  return (
    <section className="py-20 sm:py-28 px-6 sm:px-12" data-testid="rsvp-section">
      <div className="max-w-xl mx-auto">
        <p className="eyebrow mb-4 text-center">RSVP</p>
        <h2 className="font-wed text-3xl sm:text-4xl mb-3 text-center">Sarai dei nostri?</h2>
        <p className="text-neutral-500 mb-10 text-sm text-center">Facci sapere se potrai festeggiare con noi.</p>

        <form onSubmit={submit} className="space-y-5 bg-white border rounded-3xl p-6 sm:p-8" style={{ borderColor: "#e3ddd2" }}>
          <div>
            <label className="label-wed">Nome e cognome *</label>
            <input className="input-wed" value={form.nome} onChange={(e) => set("nome", e.target.value)} placeholder="Mario Rossi" data-testid="rsvp-name-input" />
          </div>
          <div className="grid sm:grid-cols-2 gap-5">
            <div>
              <label className="label-wed">Email</label>
              <input className="input-wed" type="email" value={form.email} onChange={(e) => set("email", e.target.value)} placeholder="mario@email.it" data-testid="rsvp-email-input" />
            </div>
            <div>
              <label className="label-wed">Telefono</label>
              <input className="input-wed" value={form.telefono} onChange={(e) => set("telefono", e.target.value)} placeholder="333 1234567" data-testid="rsvp-phone-input" />
            </div>
          </div>
          <div>
            <label className="label-wed">Parteciperai? *</label>
            <div className="grid grid-cols-2 gap-3">
              <button
                type="button"
                onClick={() => set("conferma", true)}
                className={`flex items-center justify-center gap-2 rounded-xl border py-3 text-sm font-medium transition-colors duration-200 ${form.conferma === true ? "text-white" : "bg-white text-neutral-600"}`}
                style={form.conferma === true ? { background: "var(--wed-primary)", borderColor: "var(--wed-primary)" } : { borderColor: "#e3ddd2" }}
                data-testid="rsvp-yes-button"
              >
                <Heart size={16} /> Ci sarò
              </button>
              <button
                type="button"
                onClick={() => set("conferma", false)}
                className={`flex items-center justify-center gap-2 rounded-xl border py-3 text-sm font-medium transition-colors duration-200 ${form.conferma === false ? "bg-neutral-800 text-white border-neutral-800" : "bg-white text-neutral-600"}`}
                style={form.conferma === false ? {} : { borderColor: "#e3ddd2" }}
                data-testid="rsvp-no-button"
              >
                <HeartCrack size={16} /> Non ci sarò
              </button>
            </div>
          </div>
          {form.conferma === true && (
            <>
              {askMoments && (
                <div>
                  <label className="label-wed">A quali momenti parteciperai?</label>
                  <div className="flex flex-wrap gap-2">
                    {wedding.moments.map((m) => (
                      <button
                        key={m.id}
                        type="button"
                        onClick={() => toggleMoment(m.id)}
                        className="text-sm rounded-full border px-3.5 py-2 transition-colors duration-150"
                        style={form.moments.includes(m.id) ? { background: "var(--wed-primary)", borderColor: "var(--wed-primary)", color: "white" } : { borderColor: "#e3ddd2", color: "#525252" }}
                        data-testid={`rsvp-moment-${m.id}`}
                      >
                        {m.label}
                      </button>
                    ))}
                  </div>
                </div>
              )}
              <div>
                <label className="label-wed">Numero accompagnatori</label>
                <select className="input-wed" value={form.num_ospiti_extra} onChange={(e) => set("num_ospiti_extra", e.target.value)} data-testid="rsvp-plusone-select">
                  {[0, 1, 2, 3, 4].map((n) => (
                    <option key={n} value={n}>{n}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="label-wed">Allergie o intolleranze</label>
                <textarea className="input-wed" rows={3} value={form.allergie_note} onChange={(e) => set("allergie_note", e.target.value)} placeholder="Es. senza glutine, vegetariano…" data-testid="rsvp-allergies-input" />
              </div>
            </>
          )}
          <button type="submit" className="btn-wed w-full" disabled={sending} data-testid="rsvp-submit-button">
            {sending ? "Invio in corso…" : "Invia risposta"}
          </button>
        </form>
      </div>
    </section>
  );
}
